import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { api, type AuditEntity, type AuditEntry } from '../api.js';

// Fields that change on every write and carry no information for a reader.
const ignoredFields = new Set(['id', 'created_at', 'updated_at']);

/**
 * Change history of one object, opened from a "History" button:
 * <audit-dialog></audit-dialog> then dialog.show('client', 5, 'Acme GmbH').
 */
@customElement('audit-dialog')
export class AuditDialog extends LitElement {
  static styles = css`
    .entry {
      padding: var(--wa-space-s) 0;
      border-bottom: 1px solid var(--wa-color-surface-border);
    }

    .entry:last-child {
      border-bottom: none;
    }

    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--wa-space-xs);
      font-size: var(--wa-font-size-s);
    }

    .when {
      color: var(--wa-color-text-quiet);
      font-variant-numeric: tabular-nums;
    }

    .diff {
      margin: var(--wa-space-2xs) 0 0;
      padding: 0;
      list-style: none;
      font-size: var(--wa-font-size-xs);
      color: var(--wa-color-text-quiet);
    }

    .diff code {
      color: var(--wa-color-text-normal);
      word-break: break-word;
    }

    .empty {
      padding: var(--wa-space-m);
      text-align: center;
      color: var(--wa-color-text-quiet);
    }
  `;

  @state() private isOpen = false;
  @state() private heading = '';
  @state() private loading = false;
  @state() private error = '';
  @state() private entries: AuditEntry[] = [];

  async show(entity: AuditEntity, id: number, label: string) {
    this.heading = `History · ${label}`;
    this.entries = [];
    this.error = '';
    this.isOpen = true;
    this.loading = true;
    try {
      this.entries = await api.get<AuditEntry[]>(`/api/audit?entity=${entity}&id=${id}&limit=100`);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.isOpen = false;
  };

  render() {
    return html`
      <wa-dialog label=${this.heading} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(44rem, calc(100vw - 1.5rem));">
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
        ${this.loading ? html`<wa-spinner></wa-spinner>` : nothing}
        ${!this.loading && !this.error && this.entries.length === 0 ? html`<div class="empty">No recorded changes.</div>` : nothing}
        ${this.entries.map((entry) => this.renderEntry(entry))}
      </wa-dialog>
    `;
  }

  private renderEntry(entry: AuditEntry) {
    const changes = entry.action === 'update' ? diff(entry.before, entry.after) : [];
    return html`
      <div class="entry">
        <div class="head">
          <span class="when">${formatWhen(entry.at)}</span>
          <wa-tag size="small" variant=${actionVariant(entry.action)}>${entry.action}</wa-tag>
          <span>${entry.summary}</span>
          <span class="when">· ${entry.actor}</span>
        </div>
        ${changes.length > 0
          ? html`
              <ul class="diff">
                ${changes.map((c) => html`<li>${c.field}: <code>${c.before}</code> → <code>${c.after}</code></li>`)}
              </ul>
            `
          : entry.action === 'update'
            ? html`<ul class="diff"><li>Saved without changes</li></ul>`
            : nothing}
      </div>
    `;
  }
}

interface Change {
  field: string;
  before: string;
  after: string;
}

/** Fields whose value differs between two API snapshots. */
function diff(before: unknown, after: unknown): Change[] {
  const a = (before ?? {}) as Record<string, unknown>;
  const b = (after ?? {}) as Record<string, unknown>;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: Change[] = [];
  for (const key of [...keys].sort()) {
    if (ignoredFields.has(key)) continue;
    const x = JSON.stringify(a[key] ?? null);
    const y = JSON.stringify(b[key] ?? null);
    if (x !== y) out.push({ field: key, before: show(a[key]), after: show(b[key]) });
  }
  return out;
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v.length === 0 ? '—' : v.map((item) => (typeof item === 'object' && item && 'label' in item ? String((item as { label: unknown }).label) : JSON.stringify(item))).join(', ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function actionVariant(action: string): 'success' | 'brand' | 'danger' | 'neutral' {
  switch (action) {
    case 'create':
    case 'upload':
      return 'success';
    case 'update':
      return 'brand';
    case 'delete':
      return 'danger';
    default:
      return 'neutral';
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

declare global {
  interface HTMLElementTagNameMap {
    'audit-dialog': AuditDialog;
  }
}
